import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './stores/auth';
import { PlatformAdminLayout } from './layouts/PlatformAdminLayout';
import { LoginPage } from './pages/Login';
import { OAuthCallbackPage } from './pages/OAuthCallback';
import { DashboardPage } from './pages/Dashboard';
import { TenantsPage } from './pages/Tenants';
import { TenantDetailPage } from './pages/TenantDetail';
import { IntegrationsPage } from './pages/Integrations';
import { AaimServicesPage } from './pages/AaimServices';
import { SystemHealthPage } from './pages/SystemHealth';
import { SettingsPage } from './pages/Settings';
import { FeatureFlagsPage } from './pages/FeatureFlags';
import { AuditLogsPage } from './pages/AuditLogs';
import { DataBrowserPage } from './pages/DataBrowserPage';
import { SupportCasesPage } from './pages/support/SupportCasesPage';
import { SupportCaseDetailPage } from './pages/support/SupportCaseDetailPage';
import { SupportSlaPage } from './pages/support/SupportSlaPage';
import { SupportAgentsPage } from './pages/support/SupportAgentsPage';
import { RecoveryCasesPage } from './pages/support/RecoveryCasesPage';
// LegalAutomation archived 2026-04-09: superseded by pages/ucc/*
// SignaturesPage deleted 2026-04-29 (Sprint C11 Lane 5): the stand-alone
// Electronic Signatures page in platform-admin had no backend wiring and
// shipped fabricated mock data. The signature flow is now part of the UCC
// filing lifecycle (pages/ucc/*) where it belongs end-to-end.
import {
  UCCDashboardPage,
  UCCFilingListPage,
  UCCFilingDetailPage,
  UCCEntitiesPage,
  UCCSubmissionsPage,
  UCCSearchPage,
  UCCJurisdictionsPage,
  UCCFeesPage,
  UCCAnalyticsPage,
  UCCCompliancePage,
} from './pages/ucc';
import { PropertyValuationsPage } from './pages/PropertyValuations';
import { TitleSearchPage } from './pages/TitleSearch';
import { LienVerificationPage } from './pages/LienVerification';
import { AttomTestInterfacePage } from './pages/AttomTestInterface';
import { IdentityProvidersPage } from './pages/IdentityProviders';
import { IdentityProviderDetailPage } from './pages/IdentityProviderDetail';
import { RolesPage } from './pages/Roles';
import { RoleDetailPage } from './pages/RoleDetail';
import { RegulatoryReadinessPage } from './pages/RegulatoryReadiness';
import { JurisdictionDetailPage } from './pages/JurisdictionDetail';
import { StrategyToolsPage } from './pages/StrategyTools';
import { PreferencesPage } from './pages/Preferences';
import { SyndicationDashboard } from './pages/syndication/SyndicationDashboard';
import { SyndicationOfferings } from './pages/syndication/SyndicationOfferings';
import { SyndicationPositions } from './pages/syndication/SyndicationPositions';
import { SyndicationCrt } from './pages/syndication/SyndicationCrt';
import { PledgesPage } from './pages/Pledges';
import { PledgeDetailPage } from './pages/PledgeDetail';
import { TasksPage } from './pages/admin/Tasks';
import { NotificationRoutingPage } from './pages/admin/NotificationRouting';
import { AssetClassDefaultsPage } from './pages/asset-classes';
import { CrtProgramSettings } from './pages/syndication/CrtProgramSettings';
import { CrtSubscriptionsQueue } from './pages/syndication/CrtSubscriptionsQueue';
// F-PORTAL-AUDIENCE-SPLIT (2026-05-17): pages relocated from admin-portal.
// Sprint E (2026-05-17): AllInstitutions retired -- Tenants is now the
// canonical roster surface. The type-segmentation insight from the legacy
// "All Institutions" lens lives in the Tenants Type column + filter.
import {
  PlatformConfigPage,
  SecurityConfigPage,
  NotificationsConfigPage,
  ApiConfigPage,
  DataConfigPage,
  IntegrationsConfigPage,
  DefaultsConfigPage,
  ProviderRateLimitsConfigPage,
  CollateralBaselinesConfigPage,
  FeatureFlagsConfigPage as PlatformFeatureFlagsConfigPage,
  LendingConfigPage,
  WorkflowRecipesConfigPage,
} from './pages/platform-config';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized } = useAuthStore();

  if (BYPASS_AUTH) {
    return <>{children}</>;
  }

  if (!isInitialized) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { initialize } = useAuthStore();

  useEffect(() => {
    // Rehydrate the user from the HttpOnly session cookie on app load.
    void initialize();
  }, [initialize]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        <Route
          element={
            <ProtectedRoute>
              <PlatformAdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="aaim-services" element={<AaimServicesPage />} />
          <Route path="system-health" element={<SystemHealthPage />} />
          {/* One General Settings route. /settings and /platform-config/general
              served the same page under two titles; /settings now redirects. */}
          <Route path="settings" element={<Navigate to="/platform-config/general" replace />} />
          <Route path="preferences" element={<PreferencesPage />} />
          <Route path="feature-flags" element={<FeatureFlagsPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="data-browser" element={<DataBrowserPage />} />
          {/* legal route removed 2026-04-09: LegalAutomationPage archived, superseded by pages/ucc/* */}
          {/* legal/signatures route removed 2026-04-29 (Sprint C11 Lane 5): SignaturesPage deleted; signature flow lives in UCC filings */}
          <Route path="legal/signatures" element={<Navigate to="/ucc/filings" replace />} />
          <Route path="ucc" element={<UCCDashboardPage />} />
          <Route path="ucc/filings" element={<UCCFilingListPage />} />
          <Route path="ucc/filings/:filingId" element={<UCCFilingDetailPage />} />
          <Route path="ucc/entities" element={<UCCEntitiesPage />} />
          <Route path="ucc/submissions" element={<UCCSubmissionsPage />} />
          <Route path="ucc/search" element={<UCCSearchPage />} />
          <Route path="ucc/jurisdictions" element={<UCCJurisdictionsPage />} />
          <Route path="ucc/fees" element={<UCCFeesPage />} />
          <Route path="ucc/analytics" element={<UCCAnalyticsPage />} />
          <Route path="ucc/compliance" element={<UCCCompliancePage />} />
          <Route path="collateral/valuations" element={<PropertyValuationsPage />} />
          <Route path="collateral/title-searches" element={<TitleSearchPage />} />
          <Route path="collateral/lien-verifications" element={<LienVerificationPage />} />
          <Route path="integrations/attom" element={<AttomTestInterfacePage />} />
          <Route path="support" element={<SupportCasesPage />} />
          <Route path="support/sla" element={<SupportSlaPage />} />
          <Route path="support/agents" element={<SupportAgentsPage />} />
          <Route path="support/recovery-cases" element={<RecoveryCasesPage />} />
          <Route path="support/:caseId" element={<SupportCaseDetailPage />} />
          <Route path="identity-providers" element={<IdentityProvidersPage />} />
          <Route path="identity-providers/:providerId" element={<IdentityProviderDetailPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="roles/:roleId" element={<RoleDetailPage />} />
          <Route path="regulatory" element={<RegulatoryReadinessPage />} />
          <Route path="regulatory/:jurisdictionCode" element={<JurisdictionDetailPage />} />
          <Route path="syndication" element={<SyndicationDashboard />} />
          <Route path="syndication/offerings" element={<SyndicationOfferings />} />
          <Route path="syndication/positions" element={<SyndicationPositions />} />
          <Route path="syndication/crt" element={<SyndicationCrt />} />
          <Route path="pledges" element={<PledgesPage />} />
          <Route path="pledges/:pledgeId" element={<PledgeDetailPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="notification-routing" element={<NotificationRoutingPage />} />
          <Route path="strategy-tools" element={<StrategyToolsPage />} />
          <Route path="asset-classes" element={<AssetClassDefaultsPage />} />
          <Route path="syndication/program-settings" element={<CrtProgramSettings />} />
          <Route path="syndication/subscriptions-queue" element={<CrtSubscriptionsQueue />} />
          {/* F-PORTAL-AUDIENCE-SPLIT (2026-05-17): platform-scoped surfaces
              relocated from admin-portal. Tenancy roster (additional lens
              on /tenants) and platform-config sub-pages live here. */}
          {/* Sprint E (2026-05-17): /institutions deleted -- canonical surface is /tenants. */}
          <Route path="institutions" element={<Navigate to="/tenants" replace />} />
          <Route path="institutions/:id" element={<Navigate to="/tenants" replace />} />
          <Route path="platform-config" element={<PlatformConfigPage />} />
          <Route path="platform-config/general" element={<SettingsPage />} />
          <Route path="platform-config/security" element={<SecurityConfigPage />} />
          <Route path="platform-config/notifications" element={<NotificationsConfigPage />} />
          <Route path="platform-config/api" element={<ApiConfigPage />} />
          <Route path="platform-config/data" element={<DataConfigPage />} />
          <Route path="platform-config/integrations" element={<IntegrationsConfigPage />} />
          <Route path="platform-config/defaults" element={<DefaultsConfigPage />} />
          <Route path="platform-config/rate-limits" element={<ProviderRateLimitsConfigPage />} />
          <Route path="platform-config/lending" element={<LendingConfigPage />} />
          <Route path="platform-config/collateral-baselines" element={<CollateralBaselinesConfigPage />} />
          <Route path="platform-config/feature-flags" element={<PlatformFeatureFlagsConfigPage />} />
          <Route path="platform-config/workflow-recipes" element={<WorkflowRecipesConfigPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
